import { format } from "date-fns";
import { useMemo } from "react";

import Typography from "@mui/material/Typography";
import { DataGridPro, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";

import RowActionsMenu from "@components/RowActionsMenu";
import ColumnHeader from "@components/Table/ColumnHeader";
import { getRowClassName, tableStyles } from "@components/Table/tableStyles";

import { OrganizationUsersType, OrganizationUserType } from "@interfaces/organization.type";

import TableOverlay from "@pages/Companies/components/TableOverlay";

import { IArchiveUserMutationType, useArchiveUserQuery } from "@queries/organization.query";

import { PopupActions } from "@store/popupStore";

import theme from "@theme/index";

interface IUserTableTypedProperties {
  isLoading?: boolean;
  organizationId: string;
  searchQuery: string;
  shouldUseTabHeight?: boolean;
  total?: number;
  usersData: OrganizationUsersType; // User type as needed
}

const handleArchiveUser = (
  userAccountId: string,
  firstName: string,
  lastName: string,
  organizationId: string,
  archiveUser: IArchiveUserMutationType,
) => {
  PopupActions.openConfirmation({
    content: (
      <Typography variant="body2">
        Are you sure you want to{" "}
        <strong>
          remove {firstName} {lastName}
        </strong>{" "}
        from your Organization? You{" "}
        <strong>
          {" "}
          <u>cannot</u>{" "}
        </strong>
        undo this action. All technologies assigned to this user will be marked as unassigned.
      </Typography>
    ),
    customOnConfirmText: "Remove",
    isDanger: true,
    onConfirm: () => {
      void archiveUser({ firstName, lastName, organizationId, userAccountId });
    },
    title: "Remove User from Organization",
  });
};

export default function UsersTable({
  organizationId,
  searchQuery,
  shouldUseTabHeight = false,
  usersData,
}: IUserTableTypedProperties) {
  const { mutateAsync: archiveUser } = useArchiveUserQuery();

  // Filter the data based on the search query
  const filteredData = useMemo(() => {
    const lowerCaseQuery = searchQuery.toLowerCase();

    return usersData.filter(
      (user) =>
        user.firstName?.toLowerCase().includes(lowerCaseQuery) ||
        user.lastName?.toLowerCase().includes(lowerCaseQuery) ||
        user.email?.toLowerCase().includes(lowerCaseQuery),
    );
  }, [usersData, searchQuery]);

  // Define the columns
  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "menuIdentifier",
        flexGrow: 0.1,
        renderCell: (parameters: GridRenderCellParams<OrganizationUserType>) => {
          const usersTableRowMenuItems = [
            {
              func: () => {
                void handleArchiveUser(
                  parameters.row.userAccountId,
                  parameters.row.firstName,
                  parameters.row.lastName,
                  organizationId,
                  archiveUser,
                );
              },
              label: "Delete User",
            },
          ];

          return <RowActionsMenu rowMenuItems={usersTableRowMenuItems} />;
        },
        renderHeader: () => <ColumnHeader title="" tooltip="" />,
      },
      {
        field: "Name",
        flex: 2,
        renderCell: (parameters: GridRenderCellParams<OrganizationUserType>) => (
          <Typography variant="subtitle2">
            {parameters.row.firstName} {parameters.row.lastName}
          </Typography>
        ),
        renderHeader: () => <ColumnHeader title="Name" tooltip="" />,
      },
      {
        field: "email",
        flex: 2,
        renderCell: (parameters: GridRenderCellParams<OrganizationUserType>) => (
          <Typography variant="subtitle2">{parameters.row.email}</Typography>
        ),
        renderHeader: () => <ColumnHeader title="Email" tooltip="" />,
      },
      {
        field: "assignedTechnologies",
        flex: 2,
        renderCell: (parameters: GridRenderCellParams<OrganizationUserType>) => (
          <Typography variant="subtitle2">{parameters.row.assignedTechnologies?.toString() || "0"}</Typography>
        ),
        renderHeader: () => <ColumnHeader title="Assigned Technologies" tooltip="" />,
      },
      {
        field: "Created",
        flex: 2,
        renderCell: (parameters: GridRenderCellParams<OrganizationUserType>) => (
          <Typography variant="subtitle2">{format(new Date(parameters.row.created), "MM/dd/yy")}</Typography>
        ),
        renderHeader: () => <ColumnHeader title="Created" tooltip="" />,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveUser]);

  return (
    <DataGridPro
      className={shouldUseTabHeight ? "tab-height" : ""}
      columns={columns}
      disableColumnMenu={true}
      disableRowSelectionOnClick
      getRowClassName={getRowClassName}
      getRowId={(row: (typeof usersData)[number]) => row.userAccountId} // Ensure each row has a unique id
      initialState={{
        pagination: { paginationModel: { page: 0, pageSize: 50 } },
        sorting: { sortModel: [{ field: "created", sort: "desc" }] },
      }}
      pageSizeOptions={[50, 100, 200]} // Options for pagination
      pagination
      paginationMode="client" // Set to 'server' for server-side pagination
      rows={filteredData} // Use the filtered data
      slots={{
        loadingOverlay: () => <TableOverlay subtitle="This can take up to one minute." title="Getting Users" />,
      }}
      sortingOrder={["desc", "asc"]}
      sx={{ ...tableStyles.root }}
    />
  );
}
